/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-var-requires */
import Vue from 'vue';
import App from './App.vue';
import vuetify from './plugins/vuetify.ts';
import 'roboto-fontface/css/roboto/roboto-fontface.css';
import '@fontsource/roboto-mono';
import '@mdi/font/css/materialdesignicons.css';
import './coi/campingonlineicons.css';
import 'flag-icons/css/flag-icons.min.css';
import router from './router/index.ts';
import { routeRequiresAuthentication } from './router/routeAccess.ts';
import store from './store/index.ts';
import * as Sentry from '@sentry/vue';
import './registerServiceWorker';
import { MutationCache, QueryCache, QueryClient, VueQueryPlugin } from '@tanstack/vue-query';
import './dayjs.ts'; // Ladda dayjs och plugins
import { snackbar } from './store/snackbar.ts';
import { ZodError } from 'zod';
import { normalizeScannableScrInput } from './utils/scrScanner';
import { translateSharedDashboard } from './localization/sharedDashboard';
Vue.prototype.$dashboardText = translateSharedDashboard;
Vue.prototype.$dashboardCardText = (componentName, fallback) => {
    const state = store.state;
    return state.stats.dashboardCards.find(card => card.component_name === componentName)?.name || fallback;
};
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: 1,
            // Data som hämtats nyss visas från cachen i stället för att hämtas igen vid
            // montering och fönsterfokus. Mutationer ogiltigförklarar allt nedan.
            staleTime: 30000
        }
    },
    mutationCache: new MutationCache({
        onSettled: (data, error, vars, result, mutation, ctx) => {
            ctx.client.invalidateQueries();
        },
        onError: (error, vars, res, mutation) => {
            if (error instanceof ZodError) {
                if (process.env.NODE_ENV === 'production') {
                    snackbar.error('Kunde inte spara data. Vänligen försök igen.');
                    console.error('Mutation zod validation error', {
                        mutationKey: mutation.options.mutationKey,
                        issues: error.issues,
                        data: mutation.state.data
                    });
                    Sentry.captureException(error, {
                        tags: { type: 'zod_validation', context: 'mutation' },
                        extra: {
                            mutationKey: mutation.options.mutationKey,
                            issues: error.issues
                        }
                    });
                }
                else {
                    snackbar.error(`Zod-fel (mutation): ${mutation.options.mutationKey?.join('.')}`);
                    console.error('Response zod validation error', {
                        mutationKey: mutation.options.mutationKey,
                        issues: error.issues,
                        data: mutation.state.data
                    });
                }
                return;
            }
            console.error('[QueryClient] Mutation error:', {
                name: '[QueryClient] MutationError',
                mutationKey: mutation.options.mutationKey,
                error
            });
            if (process.env.NODE_ENV === 'production' &&
                typeof error === 'object' &&
                error !== null) {
                Sentry.captureException(error, {
                    tags: {
                        mutationKey: JSON.stringify(mutation.options.mutationKey)
                    },
                    extra: {
                        variables: vars
                    }
                });
            }
            // Http-klienten hanterar snackbars för övriga fel som status = error
        }
    }),
    queryCache: new QueryCache({
        onError: (error, query) => {
            if (error instanceof ZodError) {
                if (process.env.NODE_ENV === 'production') {
                    snackbar.error('Kunde inte hämta data. Vänligen försök igen.');
                    console.error('Response zod validation error', {
                        queryKey: query.queryKey,
                        issues: error.issues,
                        data: query.state.data
                    });
                    Sentry.captureException(error, {
                        tags: { type: 'zod_validation', context: 'query' },
                        extra: {
                            queryKey: query.queryKey,
                            issues: error.issues
                        }
                    });
                }
                else {
                    snackbar.error(`Zod-fel (query): ${query.queryKey.join('.')}`);
                    console.error('Response zod validation error', {
                        queryKey: query.queryKey,
                        issues: error.issues,
                        data: query.state.data
                    });
                }
                return;
            }
            console.error('[QueryClient] Query error:', {
                name: '[QueryClient] QueryError',
                queryKey: query.queryKey,
                error
            });
            if (process.env.NODE_ENV === 'production' &&
                typeof error === 'object' &&
                error !== null) {
                Sentry.captureException(error, {
                    tags: {
                        queryKey: JSON.stringify(query.queryKey)
                    },
                    extra: {
                        queryState: query.state
                    }
                });
            }
            // Http-klienten hanterar snackbars för övriga fel som status = error
        }
    })
});
Vue.use(VueQueryPlugin, { queryClient });
// Fel visas i console lokalt och hos Sentry i produktion, gör det mycket lättare för oss att se vad som har hänt hos kunderna
if (process.env.NODE_ENV === 'production') {
    Sentry.init({
        Vue,
        dsn: 'https://44776c2db953484499d3cc39fa289797@o1418389.ingest.sentry.io/6761519',
        integrations: [
            Sentry.browserTracingIntegration({
                router
            })
        ],
        // @ts-expect-error - Dones not exist in types but might still work
        logErrors: true,
        tracingOrigins: ['localhost', /^.*\.campingonline\.se$/, 'app.campingonline.se', /^\//],
        tracesSampleRate: 0.1, // räcker med att vi har ett urval på 10% för prestandadelen
        release: process.env.VUE_APP_VERSION,
        beforeSend(event, hint) {
            const error = hint.originalException;
            if (error instanceof Error && (error.name === 'NavigationDuplicated' || error.message.includes('Navigation cancelled'))) {
                return null;
            }
            return event;
        }
    });
}
Vue.config.productionTip = false;
window.conversion = require('@/plugins/conversion.js').default;
window.enrich = require('@/plugins/enrich.js').default;
window.helper = require('@/plugins/helper.js').default;
window.mainVM = new Vue({
    // @ts-expect-error - vuetify is not typed
    vuetify: vuetify,
    router,
    store,
    created() {
        // console.log('Vue created')
        router.afterEach((to, from) => {
            store.dispatch('navigated', { from, to });
            if (!routeRequiresAuthentication(from) && routeRequiresAuthentication(to)) {
                Vue.nextTick(() => store.dispatch('initialiseAuthentication'));
            }
        });
        store.dispatch('refreshNow');
        setInterval(function () {
            store.dispatch('refreshNow');
        }, 1000);
        const initialRoute = router.resolve(window.location.pathname).route;
        store.dispatch('initialiseStore', {
            initialiseAuthentication: routeRequiresAuthentication(initialRoute)
        });
    },
    render: h => h(App)
}).$mount('#app');
let scrInputCandidate = null;
function getScrInputTarget(target) {
    if ((target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) && !target.disabled && !target.readOnly) {
        return target;
    }
    return null;
}
function takeScrInputCandidate() {
    const candidate = scrInputCandidate;
    if (candidate?.idleTimer != null) {
        window.clearTimeout(candidate.idleTimer);
    }
    scrInputCandidate = null;
    return candidate;
}
function setScrInputValue(candidate, value, selectionStart, selectionEnd = selectionStart, emitInput = true) {
    if (!candidate.target?.isConnected) {
        return;
    }
    candidate.target.value = value;
    try {
        candidate.target.setSelectionRange(selectionStart, selectionEnd);
    }
    catch {
        // Some input types do not support selection ranges.
    }
    if (emitInput) {
        candidate.target.dispatchEvent(new Event('input', { bubbles: true }));
    }
}
function restoreScrInput(candidate) {
    if (!candidate.target?.isConnected || !window.scannerVM) {
        return;
    }
    window.scannerVM.restoringInput = true;
    try {
        setScrInputValue(candidate, candidate.valueBefore, candidate.selectionStart, candidate.selectionEnd);
    }
    finally {
        Vue.nextTick(() => {
            if (window.scannerVM) {
                window.scannerVM.restoringInput = false;
            }
        });
    }
}
function syncScrInput(candidate) {
    const value = candidate.valueBefore.slice(0, candidate.selectionStart) + candidate.input + candidate.valueBefore.slice(candidate.selectionEnd);
    setScrInputValue(candidate, value, candidate.selectionStart + candidate.input.length);
}
function flushScrInputCandidate() {
    const candidate = takeScrInputCandidate();
    if (candidate) {
        syncScrInput(candidate);
    }
}
function showScrScanError() {
    window.mainVM.$store.dispatch('snackbars/createSnackbar', {
        color: 'error',
        text: 'Skanning misslyckades, försök igen'
    });
}
function openBookingFromScannedCard(bookingId) {
    if (window.mainVM.$store.state.currentPageName === 'PosRegister') {
        window.mainVM.$emit('scanned-booking-in-register', bookingId);
        return;
    }
    window.mainVM.$store.dispatch('booking/openDialogById', { bookingId });
}
window.scannerVM = new Vue({
    data: {
        events: [],
        restoringInput: false,
        testMode: false, // Kan aktiveras från sidan enheter
        testModeLog: [], // Logg av tangentryck i testläge, visas på sidan enheter
        logToConsole: false, // true för att köra console.log
        hasTally: true, // TODO: CO-1135 hårdkodat
        respondTo: '',
        respondToPayload: null,
        requestedMultipleInputs: false,
        currentPageName: '',
        maxTimeBetweenEvents: 100,
        scrInputIdleTimeout: 1000
    },
    methods: {
        toggleTestMode(val) {
            this.testMode = val;
            if (!val) {
                this.testModeLog = [];
            }
        },
        onkeydown(e) {
            // Loggning för test
            if (this.logToConsole) {
                console.log(`keydown - key: ${e.key}, code: ${e.code}, keyCode: ${e.keyCode}, timeStamp: ${e.timeStamp}`);
            }
            if (this.testMode) {
                // @ts-expect-error - dont know
                this.testModeLog.push(e);
                return;
            }
            const isScrMode = this.respondTo.startsWith('scr/');
            if (isScrMode) {
                this.onScrKeydown(e);
            }
            else {
                flushScrInputCandidate();
                // Sparar bara siffor input just nu, inkl hexadecimala tal
                const isDigitOrHex = (e.keyCode >= 48 && e.keyCode <= 57) || (e.keyCode >= 65 && e.keyCode <= 70);
                if (isDigitOrHex) {
                    const previousEvent = this.events[this.events.length - 1];
                    if (previousEvent && e.timeStamp - previousEvent.timeStamp > this.maxTimeBetweenEvents) {
                        this.events = [];
                    }
                    this.events.push(e);
                }
            }
            // "Avslutar" vid Enter eller Tab (vissa skannrar skickar Tab istället för Enter)
            if (!isScrMode && (e.keyCode === 13 || e.keyCode === 9)) {
                this.onenter(e);
            }
            // Ctrl + f
            if (e.keyCode === 70 && e.ctrlKey) {
                e.preventDefault();
                store.commit('SET_GLOBAL_SEARCH_STRING', ''); // Öppnar sökdialogen
            }
        },
        onScrKeydown(e) {
            if (e.keyCode === 13 || e.keyCode === 9) {
                this.submitScrInput(e);
                return;
            }
            const isModifier = ['Shift', 'Control', 'Alt', 'AltGraph', 'Meta', 'CapsLock'].includes(e.key);
            if (isModifier) {
                return;
            }
            const usesShortcutModifier = e.metaKey || (e.ctrlKey && !e.altKey);
            const isPrintable = !e.isComposing && !usesShortcutModifier && e.key.length === 1;
            if (!isPrintable) {
                flushScrInputCandidate();
                return;
            }
            const target = getScrInputTarget(e.target);
            const elapsed = scrInputCandidate ? e.timeStamp - scrInputCandidate.lastTimeStamp : 0;
            if (scrInputCandidate && (scrInputCandidate.target !== target || (scrInputCandidate.input.length < 3 && elapsed > this.maxTimeBetweenEvents))) {
                flushScrInputCandidate();
            }
            if (!scrInputCandidate) {
                scrInputCandidate = {
                    input: '',
                    lastTimeStamp: e.timeStamp,
                    target,
                    valueBefore: target?.value ?? '',
                    selectionStart: target?.selectionStart ?? target?.value.length ?? 0,
                    selectionEnd: target?.selectionEnd ?? target?.value.length ?? 0,
                    idleTimer: null
                };
            }
            const candidate = scrInputCandidate;
            const ckePrefixMismatch = (candidate.input === 'e' && e.key !== 'y') || (candidate.input === 'ey' && e.key !== 'J');
            candidate.input += e.key;
            candidate.lastTimeStamp = e.timeStamp;
            if (ckePrefixMismatch) {
                e.preventDefault();
                flushScrInputCandidate();
                return;
            }
            const isCkePrefix = candidate.input === 'e' || candidate.input === 'ey';
            const isCkeScan = candidate.input.startsWith('eyJ');
            if (isCkePrefix || candidate.input.length >= 3) {
                e.preventDefault();
                if (!isCkePrefix && !isCkeScan && candidate.target) {
                    const value = candidate.valueBefore.slice(0, candidate.selectionStart) + candidate.input + candidate.valueBefore.slice(candidate.selectionEnd);
                    setScrInputValue(candidate, value, candidate.selectionStart + candidate.input.length, undefined, false);
                }
            }
            if (candidate.idleTimer !== null) {
                window.clearTimeout(candidate.idleTimer);
            }
            if (isCkePrefix || candidate.input.length >= 3) {
                candidate.idleTimer = window.setTimeout(() => {
                    if (scrInputCandidate === candidate) {
                        flushScrInputCandidate();
                    }
                }, isCkePrefix ? this.maxTimeBetweenEvents : this.scrInputIdleTimeout);
            }
        },
        submitScrInput(e) {
            const candidate = takeScrInputCandidate();
            if (!candidate) {
                return;
            }
            if (candidate.input.length < 3) {
                syncScrInput(candidate);
                return;
            }
            const normalizedScan = normalizeScannableScrInput(candidate.input);
            const isCorruptScan = !normalizedScan && candidate.input.length > 100;
            if (!normalizedScan && candidate.target && !isCorruptScan) {
                syncScrInput(candidate);
                return;
            }
            e.preventDefault();
            e.stopPropagation();
            restoreScrInput(candidate);
            const respondTo = this.respondTo;
            const payload = this.respondToPayload;
            if (!this.requestedMultipleInputs) {
                this.respondTo = '';
                this.respondToPayload = null;
            }
            if (isCorruptScan) {
                window.mainVM.$store.commit('scr/SET_SEARCH_RESULT', { source: 'none', query: candidate.input });
                showScrScanError();
                return;
            }
            const input = normalizedScan ?? (!candidate.target && /^00\d{8,}$/.test(candidate.input)
                ? `+${candidate.input.slice(2)}`
                : candidate.input);
            Promise.resolve(window.mainVM.$store.dispatch(respondTo, {
                input,
                param: payload
            })).then((result) => {
                if (!result || result.source === 'none') {
                    showScrScanError();
                }
            }).catch(() => {
                showScrScanError();
            });
        },
        onenter(e) {
            // Loggning för test
            if (this.logToConsole) {
                console.log('onenter', this.events.length);
                if (this.events.length >= 8) {
                    console.log('ts diff', this.events[7].timeStamp - this.events[0].timeStamp);
                }
            }
            /*  Validering:
                Det har kommit minst 8 tecken med max 100ms mellan varje, följt av Enter ->
                  Det finns en respondTo ->
                    skicka svaret till den metoden
                  Det finns ingen respondTo ->
                    Om på kassasidan ELLER campingen använder inte Tally (antag alltid att det är en streckkod) ->
                      Strängen matchar en streckkod på en existerande produkt ->
                        *Lägg produkten i varukorgen*
                      Strängen matchar inte en streckkod på en existerande produkt ->
                        *Öppna produkt formuläret*
                    Om på någon annan sida ->
                      Strängen matchar en streckkod på en existerande produkt ->
                        *Lägg produkten i varukorgen*
                      Strängen matchar inte en streckkod på en existerande produkt ->
                        *Skicka till server för att identifiera tallykort*
      
                Behövs det lösningar för när det inte stämmer?
                  Vad kan "inte stämma"?
                    Användaren skannar tallykort på kassasidan => Ska vi kontrollera om input innehåller bokstäver också? eller bättre att stödja streckkoder med bokstäver kanske?
                    Användaren försöker lägga till ny produkt på annan sida än kassan => Ska vi kontrollera längden på input, tally är alltid 8 eller 10 tecken, kan detta komma att ändras?
            */
            if (this.events.length >= 8) {
                e.preventDefault();
                let input = '';
                this.events.forEach(event => {
                    input += event.key;
                });
                if (this.logToConsole) {
                    console.log('input', input);
                }
                this.events = []; // Rensar events efter Enter
                if (this.respondTo !== '') {
                    window.mainVM.$store.dispatch(this.respondTo, { input, param: this.respondToPayload });
                    if (!this.requestedMultipleInputs) {
                        // Återställer respondTo efter första input om inte specifikt efterfrågade flera inputs
                        this.respondTo = '';
                        this.respondToPayload = null;
                    }
                }
                else {
                    if (!this.hasTally) {
                        // Campingen använder inte tally
                        window.mainVM.$store.dispatch('product/scannedBarcode', input);
                    }
                    else {
                        // kolla om input matchar mot en streckkod på existerande produkter
                        const product = window.mainVM.$store.state.product.products.find((prod) => prod.barcode === input);
                        if (product) {
                            // Produkt finns
                            if (this.currentPageName === 'Products') {
                                // På produktsidan, öppna product-dialog
                                window.mainVM.$store.dispatch('product/openDialogById', { productId: product.id });
                            }
                            else {
                                // Annars lägg i varukorgen
                                window.mainVM.$store.dispatch('product/scannedBarcode', input);
                            }
                            return false;
                        }
                        // om inte finns på existerande produkt, kolla om finns bland tallykorten
                        const cardUid = parseInt(input, 16);
                        if (!isNaN(cardUid)) {
                            window.mainVM.$store.commit('tally/ADD_RECENTLY_SCANNED_UID', cardUid);
                            const tallyCard = window.mainVM.$store.state.tally.activeTallyCards.find((card) => card.card_uid === cardUid);
                            if (tallyCard) {
                                openBookingFromScannedCard(tallyCard.booking_id);
                                return false;
                            }
                        }
                        if (this.currentPageName === 'PosRegister' || /^[0-9]+$/.test(input)) {
                            // I kassan behandlas alla oidentifierade koder som produktstreckkoder. På andra sidor görs det bara för numeriska koder.
                            window.mainVM.$store.dispatch('product/scannedBarcode', input);
                            return false;
                        }
                        window.mainVM.$store.dispatch('snackbars/createSnackbar', {
                            color: 'warning',
                            text: 'Oidentifierat objekt'
                        });
                    }
                }
            }
            else {
                // Loggning för test
                if (this.logToConsole) {
                    console.log('No code');
                }
            }
        },
        requestInput(respondTo, payload = null, multiple = false) {
            flushScrInputCandidate();
            this.respondTo = respondTo;
            this.respondToPayload = payload;
            this.requestedMultipleInputs = multiple;
        },
        abortInputRequest(respondTo) {
            if (this.respondTo === respondTo) {
                flushScrInputCandidate();
                this.respondTo = '';
                this.respondToPayload = null;
                this.requestedMultipleInputs = false;
            }
        },
        setCurrentPageName(val) {
            this.currentPageName = val;
        }
    },
    created() {
        document.addEventListener('keydown', this.onkeydown, true);
    }
});
// Smartcard reader - global listener, always on
// Mirrors scannerVM tally logic but for NFC/contact card readers
if (window.smartcard) {
    try {
        window.smartcard.start();
    }
    catch (e) {
        // Silently fail - no reader is not an issue
    }
    window.smartcard.on('card', (card) => {
        const cardNumeric = card.numeric;
        const cardHex = cardNumeric.toString(16).toUpperCase();
        console.log('Smartcard scanned:', cardNumeric, '(hex: ' + cardHex + ')');
        // If a component has requested input (e.g. CardList adding a card), dispatch to that action
        if (window.scannerVM && window.scannerVM.respondTo === 'tally/addCard') {
            const payload = window.scannerVM.respondToPayload;
            window.mainVM.$store.dispatch('tally/addCard', { input: cardHex, param: payload });
            return;
        }
        if (window.scannerVM && window.scannerVM.respondTo === 'camptrac/addCard') {
            const payload = window.scannerVM.respondToPayload;
            window.mainVM.$store.dispatch('camptrac/addCard', { input: cardNumeric, param: payload });
            return;
        }
        // Otherwise, do the same tally card lookup as scannerVM
        window.mainVM.$store.commit('tally/ADD_RECENTLY_SCANNED_UID', cardNumeric); // REVIEW: refaktorisera tally uid till string också?
        window.mainVM.$store.commit('camptrac/ADD_RECENTLY_SCANNED_UID', cardNumeric.toString());
        const tallyCard = window.mainVM.$store.state.tally.activeTallyCards.find((tc) => tc.card_uid === cardNumeric);
        if (tallyCard) {
            openBookingFromScannedCard(tallyCard.booking_id);
            return;
        }
        const camptracCard = window.mainVM.$store.state.camptrac.activeCamptracCards.find((cc) => cc.card_uid === cardNumeric.toString());
        if (camptracCard) {
            if (camptracCard.booking_id) {
                openBookingFromScannedCard(camptracCard.booking_id);
            }
            else {
                window.mainVM.$store.dispatch('snackbars/createSnackbar', {
                    color: 'info',
                    text: 'Kortet tillhör ingen aktiv bokning'
                });
            }
            return;
        }
        window.mainVM.$store.dispatch('snackbars/createSnackbar', {
            color: 'warning',
            text: 'Oidentifierat objekt'
        });
    });
}
